// BMD constants for the student-side runtime. Change values here and they
// propagate everywhere (timer enforcement, estimated time displays, UI copy).

// =============================================================================
// Timer. Matches spec §4.4
// =============================================================================

// Initial countdown per MCQ. After this expires, the student gets a buffer
// before the question is auto-marked incorrect.
//
// Raised from 30s to 60s on the PM's instruction. The tutor-facing card now
// tells tutors "students get 60 seconds per question", so 30 would have made
// the app contradict its own description of itself.
export const BMD_MCQ_TIMER_SECONDS = 60

// Buffer granted after the initial timer expires. If the student still doesn't
// select an answer within this window, the question is auto-skipped with
// skip_reason = 'TIMEOUT'. Left at 10s: it is grace after the advertised time,
// not part of it.
export const BMD_MCQ_BUFFER_SECONDS = 10

// 5-second extension granted to follow-up questions (why / confidence) if the
// student is still answering when normal time would have expired. See spec §4.4
// timer rules table.
export const BMD_FOLLOW_UP_EXTENSION_SECONDS = 5

// Whether the 60 seconds covers the WHOLE question - the answer and the
// follow-ups together - or whether the follow-ups get their own countdown.
//
// The PM's rule is one window: "60 seconds is for both answering the DQ and the
// why/confidence questions all together, not separate timers". So the question
// timer keeps running when an answer is picked, and the follow-ups are answered
// inside the time that is left.
//
// true  - one 60s window for everything (current)
// false - the question timer pauses on answer and the follow-ups get
//         BMD_FOLLOW_UP_TIMER_SECONDS of their own
export const BMD_FOLLOW_UPS_SHARE_QUESTION_TIMER = true

// The follow-up questions' own countdown, used only when
// BMD_FOLLOW_UPS_SHARE_QUESTION_TIMER is false.
export const BMD_FOLLOW_UP_TIMER_SECONDS = 40

// What happens when that countdown runs out. This was never decided, so it is
// a placeholder choice and is expected to change.
//
// "SUBMIT_PARTIAL"  submit the answer the student already picked, recording
//   whatever follow-up they managed. A missing reason or confidence goes in as
//   NULL, which the schema and the report already tolerate, since a skip
//   produces the same nulls. It mirrors the MCQ timer: never strand the child.
// "SKIP"  abandon the whole question with skip_reason TIMEOUT, losing the
//   answer they had already chosen.
//
// Changing this constant is the whole change; see the timeout handler in
// BmdTestPlayer.
export const BMD_FOLLOW_UP_TIMEOUT_ACTION: "SUBMIT_PARTIAL" | "SKIP" =
  "SUBMIT_PARTIAL"

// Whether the MCQ options lock once an answer is picked.
//
// Picking an answer pauses the timer and reveals the follow-ups, but the
// options stay clickable. A student can pick anything to stop the clock, think
// for as long as they want, then switch to the answer they meant.
//
// The lock is off deliberately: the student may change their mind for as long
// as the follow-up countdown is running, so the answer stays editable until
// submit or timeout.
//
// The accepted consequence is that a student can type a reason for answer A
// and then switch to B, so "why did you choose this option?" can end up
// recorded against a different option than it was written about. Set to true
// to lock again.
export const BMD_LOCK_ANSWER_AFTER_SELECT = false

// Estimated total per-question time for display purposes (e.g. "~60 min" on
// the tutor's summary card). Keep in sync with the tutor-frontend value so
// tutor and student see consistent estimates.
//
// Follows the MCQ timer rather than sitting between it and the follow-up
// countdown: the tutor-facing copy quotes 60 seconds per question, and an
// estimate that disagreed with the number on the card would be the same bug
// this change is fixing.
export const BMD_SECONDS_PER_QUESTION_ESTIMATE = 60

// =============================================================================
// Fundamental Misconception flow timing (decision F1)
// =============================================================================

// There is no BMD_FM_FLOW_TIMING toggle. One was declared here, never read
// anywhere in any repo, and contradicted the spec.
//
// Spec §5.1 is explicit: the Fundamental Misconception flow is triggered
// "after that topic's session is complete", batched across every FM-tagged
// wrong answer in the topic, not immediately after the offending question. The
// backend implements exactly that, creating FM_RETRY rows in completeBmdTopic
// and ordering them after the base pass, so there is no timing to configure.

// =============================================================================
// Skip reason to intervention
// =============================================================================

// Two consecutive TIMEOUT skips on the same dq_id trigger tutor intervention.
// Manual skips do not count toward this threshold.
export const BMD_CONSECUTIVE_TIMEOUTS_FOR_INTERVENTION = 2

// =============================================================================
// UI copy
// =============================================================================

export const BMD_COPY = Object.freeze({
  PLAYER_TITLE: "Baseline Math Diagnostics",
  // A prerequisite test is a separate activity built from what the student got
  // wrong last time, so it must not read as the baseline diagnostic. It avoids
  // the word "prerequisite", too big for a Year 2 student, and any year level:
  // a Year 6 student must never see they are being asked a Year 4 skill.
  PREREQ_PLAYER_TITLE: "Math Skill Check",
  PREREQ_SHORT_LABEL: "Skill Check",
  // The count is a STARTING size: more questions appear as the child gets
  // things wrong, so never present it as a total.
  PREREQ_QUESTIONS_LABEL: "Questions to start",
  PREREQ_START: "Start Skill Check",
  PREREQ_RESUME: "Resume Skill Check",

  LOADING: "Loading…",
  SUBMIT_ANSWER: "Submit Answer",
  SKIP_FOR_NOW: "Skip for now",

  NO_QUESTIONS: "No questions available for this diagnostics session.",
  ERROR_LOAD: "Unable to load the diagnostics session.",
  ERROR_SUBMIT: "Unable to submit your answer.",

  PICK_ANSWER: "Please select an answer before submitting.",
  ENTER_REASON: "Please tell us why you chose this option.",
  PICK_CONFIDENCE: "Please select how confident you are.",

  FOLLOW_UP_WHY: "Why did you pick that answer?",
  FOLLOW_UP_CONFIDENCE: "How sure are you?",

  // --- Section overview screen -------------------------------------------
  SECTION_LABEL: "section",
  SECTIONS_LABEL: "sections",
  ANSWERED_SUFFIX: "answered",
  OVERVIEW_SAVE_NOTE: "If you stop, your answers and your place are saved.",
  SECTION_IN_PROGRESS: "In progress",
  SECTION_NEXT: "Next",
  SECTION_DONE: "Done",
  START_PREFIX: "Start with",
  CONTINUE_PREFIX: "Continue with",

  // --- Question screen ----------------------------------------------------
  SUBMIT_ANSWER_CTA: "Submit answer",
  SKIP_SUBTEXT: "Comes back at the end of the test.",
  FOLLOW_UP_OPTIONAL: "optional",
  FOLLOW_UP_WHY_PLACEHOLDER: "How did you work it out?",

  // --- Finish screen ------------------------------------------------------
  FINISH_TITLE: "That's everything for now",
  FINISH_NOTE:
    "If your tutor adds more questions later, they'll show up here. There's nothing else to do right now.",
  FINISH_CTA: "Back to my activities",
  FINISH_STAT_QUESTIONS: "questions answered",
  FINISH_STAT_TIME: "spent",

  TIMER_EXPIRED: "Time's up! Moving on.",
  INTERVENTION_FLAGGED:
    "Your tutor has been notified. They'll review this with you.",
  // Same fact, shown inline instead of in a blocking modal. A failed second
  // attempt on a fundamental misconception should not stop the student in
  // their tracks: they keep going and the tutor picks it up later.
  INTERVENTION_NOTICE:
    "Your tutor will go through this one with you later. Keep going for now.",

  TOPIC_COMPLETE: "Topic complete.",
  SESSION_COMPLETE: "Diagnostics completed. Great work!",
  // The prerequisite test is a separate activity, so "Diagnostics completed"
  // would name the wrong thing.
  PREREQ_SESSION_COMPLETE: "Skill check completed. Great work!",
  // Shown instead of SESSION_COMPLETE when the student has answered everything
  // available but a topic still owes remediation (topic_status COMPLETED rather
  // than REMEDIATED). Congratulating them on finishing would be untrue, since
  // more work is coming, but the wording stays neutral so it does not read as
  // a verdict on how they did.
  SESSION_PAUSED_FOR_REMEDIATION:
    "That's all the questions for now. Your tutor will look at your answers and set the next step.",

  // Short, because they are now ALWAYS on screen rather than appearing only
  // under the one the student picked. Five full sentences at once would be more
  // to read than the question above them.
  CONFIDENCE_LABELS: [
    "Guessed",
    "Probably wrong",
    "Maybe right",
    "Probably right",
    "Definitely right",
  ] as const,
})
